import MetricCategoryItem, { MetricCategoryItemStrings } from 'enums/MetricCategoryItem'
import MetricSeries, { MetricSeriesStrings } from 'enums/MetricSeries'
import { YAxisGroup, SeriesType, SeriesTypeStrings } from 'enums/Charts'
import MeasureType, { MeasureTypeStrings } from 'enums/MeasureType'
import ViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import AnalysisType, { AnalysisTypeStrings } from 'enums/AnalysisType'
import { TimelineSeries } from 'schemas/TimelineSeriesSchema'
import * as measureTypeUtil from './measureTypeUtil'

export const getMeasurementYAxisInfo = (
  metricName: MetricSeriesStrings | MeasureTypeStrings,
  category: MetricCategoryItemStrings,
  analysisType: AnalysisTypeStrings
) => {
  let id
  let title
  if (category === MetricCategoryItem.NONE) {
    if (metricName === MetricSeries.NET_PROMOTER_SCORES) {
      id = YAxisGroup.SW_NET_PROMOTER_SCORES
    } else {
      id = YAxisGroup.SW_CALCULATED_SCORES
    }
    title = {
      text: measureTypeUtil.getLabel(metricName, analysisType)
    }
  } else {
    id = YAxisGroup.SW_BUZZ_POST
    title = {
      text: measureTypeUtil.getLabel(MetricSeries.TOTAL_BUZZ_POST, analysisType)
    }
  }
  return { id, title, opposite: true }
}

export const getYAxisInfo = (
  metric: MetricSeriesStrings,
  category: MetricCategoryItemStrings,
  analysisType: AnalysisTypeStrings,
  viewOption?: InsightViewOptionStrings
) => {
  switch (metric) {
    case MetricSeries.TOTAL_BUZZ:
    case MetricSeries.TOTAL_BUZZ_POST:
    case MetricSeries.POST_VIDEOS:
    case MetricSeries.POST_IMAGES:
    case MetricSeries.POST_OTHER:
    case MetricSeries.OVERALL_STAR_RATINGS_AVERAGE:
    case MetricSeries.OVERALL_SURVEY_SCORES_AVERAGE:
      return {
        id: YAxisGroup.SW_BUZZ,
        title: {
          text: measureTypeUtil.getLabel(metric, analysisType)!
        },
        opposite: true
      }

    case MetricSeries.OVERALL_STAR_RATINGS:
      if (ViewOption.STAR_RATINGS === viewOption) {
        return getMeasurementYAxisInfo(MeasureType.STAR_RATINGS, category, analysisType)
      }
      return getMeasurementYAxisInfo(metric, category, analysisType)

    case MetricSeries.OVERALL_SURVEY_SCORES:
      if (ViewOption.SURVEY_SCORES === viewOption) {
        return getMeasurementYAxisInfo(MeasureType.SURVEY_SCORES, category, analysisType)
      }
      return getMeasurementYAxisInfo(metric, category, analysisType)

    case MetricSeries.NET_PROMOTER_SCORES:
      return getMeasurementYAxisInfo(metric, category, analysisType)
    case MetricSeries.ENGAGEMENTS:
    case MetricSeries.COMMENTS:
    case MetricSeries.LIKES:
    case MetricSeries.SHARES:
    case MetricSeries.DISLIKES:
    case MetricSeries.VIEWS:
    case MetricSeries.SCORE:
      return {
        id:
          analysisType === AnalysisType.YT_SUMMARY
            ? YAxisGroup.STATISTICS
            : YAxisGroup.SW_ENGAGEMENTS,
        title: {
          text: measureTypeUtil.getLabel(metric, analysisType)
        },
        opposite: true
      }

    case MetricSeries.ENGAGEMENT_RATIO:
    case MetricSeries.ENGAGEMENT_RATIO_ONLY:
      return {
        id: YAxisGroup.SW_ENGAGEMENT_RATIO,
        title: {
          text: measureTypeUtil.getLabel(metric, analysisType)
        },
        opposite: true
      }

    case MetricSeries.POSITIVE_SENTIMENT:
    case MetricSeries.NEGATIVE_SENTIMENT:
      return {
        id: YAxisGroup.SW_SENTIMENT,
        title: { text: measureTypeUtil.getLabel(metric) },
        opposite: true
      }

    case MetricSeries.NET_SENTIMENT:
    case MetricSeries.PASSION:
      return {
        id: YAxisGroup.SW_CALCULATED_SENTIMENT,
        title: { text: measureTypeUtil.getLabel(metric) },
        opposite: true
      }

    case MetricSeries.IMPRESSIONS:
      return {
        id: YAxisGroup.SW_IMPRESSION,
        title: { text: measureTypeUtil.getLabel(metric) },
        opposite: true
      }

    case MetricSeries.UNIQUE_AUTHORS:
      return {
        id: YAxisGroup.UNIQUE_AUTHORS,
        title: { text: measureTypeUtil.getLabel(metric) },
        opposite: true
      }

    default:
      if (viewOption) {
        switch (viewOption) {
          case ViewOption.STAR_RATINGS:
          case ViewOption.SURVEY_SCORES:
            return getMeasurementYAxisInfo(metric, category, analysisType)
          default:
            break
        }
      }
      throw new Error(`cannot get YAxisInfo of unknown metric: ${metric}`)
  }
}

export const getMeasurementYAxisGroup = (
  metric: MetricSeriesStrings,
  category: MetricCategoryItemStrings
) => {
  if (category === MetricCategoryItem.NONE) {
    if (metric === MetricSeries.NET_PROMOTER_SCORES) {
      return YAxisGroup.SW_NET_PROMOTER_SCORES
    }
    return YAxisGroup.SW_CALCULATED_SCORES
  }
  return YAxisGroup.SW_BUZZ_POST
}

export const getYAxisGroup = (
  metric: MetricSeriesStrings,
  category: MetricCategoryItemStrings,
  analysisType: AnalysisTypeStrings,
  viewOption?: InsightViewOptionStrings
) => {
  switch (metric) {
    case MetricSeries.TOTAL_BUZZ:
    case MetricSeries.TOTAL_BUZZ_POST:
    case MetricSeries.OVERALL_STAR_RATINGS_AVERAGE:
    case MetricSeries.OVERALL_SURVEY_SCORES_AVERAGE:
      return YAxisGroup.SW_BUZZ
    case MetricSeries.OVERALL_STAR_RATINGS:
    case MetricSeries.OVERALL_SURVEY_SCORES:
    case MetricSeries.NET_PROMOTER_SCORES:
      return getMeasurementYAxisGroup(metric, category)
    case MetricSeries.POSITIVE_SENTIMENT:
    case MetricSeries.NEGATIVE_SENTIMENT:
      return YAxisGroup.SW_SENTIMENT
    case MetricSeries.NET_SENTIMENT:
    case MetricSeries.PASSION:
      return YAxisGroup.SW_CALCULATED_SENTIMENT
    case MetricSeries.IMPRESSIONS:
      return YAxisGroup.SW_IMPRESSION
    case MetricSeries.UNIQUE_AUTHORS:
      return YAxisGroup.UNIQUE_AUTHORS
    case MetricSeries.ENGAGEMENTS:
    case MetricSeries.COMMENTS:
    case MetricSeries.LIKES:
    case MetricSeries.SHARES:
    case MetricSeries.DISLIKES:
    case MetricSeries.VIEWS:
    case MetricSeries.SCORE:
      if (analysisType === AnalysisType.YT_SUMMARY) {
        return YAxisGroup.STATISTICS
      }
      return YAxisGroup.SW_ENGAGEMENTS

    case MetricSeries.ENGAGEMENT_RATIO:
    case MetricSeries.ENGAGEMENT_RATIO_ONLY:
      return YAxisGroup.SW_ENGAGEMENT_RATIO
    case MetricSeries.POST_VIDEOS:
    case MetricSeries.POST_IMAGES:
    case MetricSeries.POST_OTHER:
      return YAxisGroup.NONE
    default:
      if (viewOption) {
        switch (viewOption) {
          case ViewOption.STAR_RATINGS:
          case ViewOption.SURVEY_SCORES:
            return getMeasurementYAxisGroup(metric, category)

          default:
            break
        }
      }
      throw new Error(`cannot get YAxis of unknown metric: ${metric}`)
  }
}

export const getSeriesType = (series: TimelineSeries): SeriesTypeStrings => {
  if (series.isChild) {
    switch (series.type) {
      case MetricSeries.PASSION:
      case MetricSeries.NET_SENTIMENT:
        return SeriesType.LINE
      default:
        return SeriesType.AREA
    }
  }

  switch (series.type) {
    case MetricSeries.POSITIVE_SENTIMENT:
    case MetricSeries.NEGATIVE_SENTIMENT:
    case MetricSeries.IMPRESSIONS:
      return SeriesType.AREA

    default:
      return SeriesType.LINE
  }
}

export const getStacking = (series: TimelineSeries, forceOffStackOnChildMeasures?: boolean) => {
  let stacking
  const type = getSeriesType(series)

  if (SeriesType.LINE !== type && SeriesType.SPLINE !== type) {
    stacking = 'normal'
  }

  if (!series.isChild) {
    switch (series.type) {
      case MetricSeries.POSITIVE_SENTIMENT:
      case MetricSeries.NEGATIVE_SENTIMENT:
      case MetricSeries.IMPRESSIONS:
        stacking = undefined
        break
      default:
        break
    }
  }

  if (series.isChild && forceOffStackOnChildMeasures) {
    stacking = undefined
  }

  return stacking
}

export const getZIndex = (type: SeriesTypeStrings) => {
  switch (type) {
    case SeriesType.FLAGS:
      return 4
    case SeriesType.AREASPLINE:
    case SeriesType.AREA:
      return 1
    case SeriesType.COLUMN:
      return 2
    case SeriesType.LINE:
    default:
      return 3
  }
}
